import { createRef, useMemo, useState } from 'react'

import { EStatus, TServiceData, TStatus } from 'common/types'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { TEndpoint } from 'contexts/app-context/types'
import { useHasScroll } from 'common/hooks/useHasScroll'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { selectTicketFiles, verifyFiles } from 'pages/new-service/utils'
import { logBridgeError, sendError, sendSelectContent, serializeError } from 'common/helpers'
import { Routes } from 'common/constants'

import { Option } from '@interco/inter-ui/components/BottomSheet'

import {
  uploadTicketFile,
  useGetUploadUrls,
  useSendRequest,
  useUpdateRequest,
} from '../repository/NewService.repository'
import { TPreparedTicketFile, TSelectedTicketFile } from '../types/NewService.types'
import {
  getPreparedTicketFiles,
  hasPendingUploadPreparation,
  toTicketFiles,
} from './use-new-service-files'

export function useNewService({
  service = {
    subject: '',
    details: '',
    status: EStatus.OPEN.toUpperCase() as TStatus,
    openingDate: '',
    hasUpdate: false,
    protocol: '',
    lastSeen: '',
    updatedAt: '',
    caseId: '',
    externalId: '',
    additionalInfo: '',
    areaResponse: '',
  },
}: {
  service?: TServiceData
}) {
  const ref = createRef<HTMLDivElement>()

  const hasScroll = useHasScroll(ref)

  const updateMutation = useUpdateRequest({
    onFinally: () => setIsLoading(false),
  })
  const newRequestMutation = useSendRequest({
    onFinally: () => setIsLoading(false),
  })
  const uploadUrlsMutation = useGetUploadUrls()

  const { subject: subjectData, details: detailsData, externalId, protocol } = service

  const isEditService = !(protocol === '' || protocol === null || protocol === undefined)

  const [selectedValue, setSelectedValue] = useState<Option>({
    text: subjectData,
    value: subjectData,
  })
  const [textValue, setTextValue] = useState(detailsData)

  const [selectedFiles, setSelectedFiles] = useState<TSelectedTicketFile[]>([])
  const [preparedFiles, setPreparedFiles] = useState<TPreparedTicketFile[]>([])
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [aboveSizeLimit, setAboveSizeLimit] = useState(false)
  const [hasFileUploadError, setHasFileUploadError] = useState(false)
  const [uploadPercentage, setUploadPercentage] = useState(0)

  const [isLoading, setIsLoading] = useState(false)

  const { privacyCenterContent, userSelectedEntity } = useAppContext()

  const supportData = privacyCenterContent?.components?.supportData

  const newSupport = privacyCenterContent?.components?.newSupport?.content
  const isActive = !!privacyCenterContent?.components?.newSupport?.isActive

  const details = newSupport?.details
  const editPageTitle = newSupport?.editPageTitle
  const pageTitle = newSupport?.pageTitle
  const sendSupportButtonLabel = newSupport?.sendSupportButtonLabel
  const subjects = newSupport?.subjects
  const upload = newSupport?.upload

  const selectOptions: Option[] | undefined = supportData?.subjectList.map((subject) => ({
    text: subject.title,
    value: subject.title,
  }))

  useScreenFinishLoadTac({
    name: 'NewService',
    screen: Routes.NEW_SERVICE,
    action: 'NewServiceFinishLoad',
    entity: userSelectedEntity,
    shouldSend: isActive && !!newSupport,
    logLabel: 'send new service finish tac',
  })

  const hasPendingUploadUrls = hasPendingUploadPreparation(selectedFiles, preparedFiles)
  const subjectDisablesSendButton =
    newSupport?.subjects.mandatory === true && selectedValue.value === ''
  const detailsDisablesSendButton = newSupport?.details.mandatory === true && textValue === ''
  const hasFileUploadIssue =
    aboveSizeLimit || isLoadingFiles || hasFileUploadError || hasPendingUploadUrls
  const uploadDisablesSendButton =
    (newSupport?.upload.mandatory === true && selectedFiles.length === 0) || hasFileUploadIssue

  const isSendButtonDisabled = useMemo(
    () => subjectDisablesSendButton || detailsDisablesSendButton || uploadDisablesSendButton,
    [detailsDisablesSendButton, subjectDisablesSendButton, uploadDisablesSendButton],
  )

  const submitPreparedFiles = async ({
    endpoint,
    flow,
    step,
    onSubmit,
  }: {
    endpoint?: TEndpoint
    flow: string
    step: string
    onSubmit: (
      endpoint: TEndpoint,
      ticketFiles: ReturnType<typeof toTicketFiles>,
    ) => Promise<unknown>
  }) => {
    setIsLoading(true)

    if (!endpoint || hasPendingUploadUrls) {
      setHasFileUploadError(true)
      setIsLoading(false)
      return
    }

    try {
      await Promise.all(preparedFiles.map(uploadTicketFile))
    } catch (error: unknown) {
      setHasFileUploadError(true)
      setIsLoading(false)
      setUploadPercentage(0)

      try {
        await sendError({
          flow,
          step,
          screen_name: Routes.NEW_SERVICE,
          error_description: 'Failed to upload files',
          error_message: serializeError(error),
        })
      } catch (analyticsError: unknown) {
        logBridgeError(`${flow} file upload error analytics`, analyticsError)
      }

      return
    }

    onSubmit(endpoint, toTicketFiles(preparedFiles)).catch((error: unknown) => {
      logBridgeError('submit new service request', error)
    })
  }

  return {
    upload,
    details,
    subjects,
    editPageTitle,
    pageTitle,
    sendSupportButtonLabel,
    selectOptions,
    selectedValue,
    textValue,
    textInfo:
      details?.limit && details?.limitText
        ? `${textValue.length}/${details.limit} ${details.limitText}`
        : '',
    isSendButtonDisabled,
    isLoading,
    isEditService,
    title: isEditService ? editPageTitle : pageTitle,
    hasScroll,
    ref,
    isLoadingFiles,
    errorOnFiles: aboveSizeLimit || hasFileUploadError,
    uploadPercentage,
    isActive,
    onChangeText: (value: string) => {
      setTextValue(value)
    },
    onChange: (value: Option) => {
      setSelectedValue(value)
    },
    onSelectFiles: async (event: FileList) => {
      setIsLoadingFiles(true)
      setUploadPercentage(50)
      setHasFileUploadError(false)
      setPreparedFiles([])

      const selectedTicketFiles = selectTicketFiles(event)

      const { aboveSizeLimit: sizeError, totalSize } = verifyFiles(selectedTicketFiles)
      let hasUploadUrlError = false

      setAboveSizeLimit(sizeError)

      setSelectedFiles(selectedTicketFiles)

      if (!sizeError && selectedTicketFiles.length > 0) {
        try {
          const files = await getPreparedTicketFiles({
            endpoint: privacyCenterContent?.endpoints?.services?.sendServiceFiles,
            externalId,
            isEditService,
            selectedFiles: selectedTicketFiles,
            getUploadUrls: uploadUrlsMutation.mutateAsync,
          })

          if (!files) {
            hasUploadUrlError = true
            setHasFileUploadError(true)
          } else {
            setPreparedFiles(files)
          }
        } catch (error: unknown) {
          hasUploadUrlError = true
          setHasFileUploadError(true)
          logBridgeError('get new service upload urls', error)
        }
      }

      try {
        await sendSelectContent({
          flow: 'new_service',
          step: 'my_service_requests',
          screen_name: Routes.MY_SERVICE_REQUESTS,
          element_name: 'uploadFiles',
          element_type: 'upload',
          parent_name: 'NewService',
          scenario: JSON.stringify({
            screen: Routes.MY_SERVICE_REQUESTS,
            product: userSelectedEntity,
            sizeError: `${sizeError}`,
            totalSize: `${totalSize} MB`,
            isEdit: `${isEditService}`,
            subject: (isEditService ? editPageTitle : pageTitle) ?? '',
            ref_type: 'upload',
            content_action: 'upload',
          }),
        })
      } catch (error: unknown) {
        logBridgeError('send new service upload tic', error)
      }

      setTimeout(() => {
        setIsLoadingFiles(false)
        setUploadPercentage(sizeError || hasUploadUrlError ? 0 : 100)
      }, 500)
    },
    onDelete: () => {
      setSelectedFiles([])
      setPreparedFiles([])
      setIsLoadingFiles(false)
      setAboveSizeLimit(false)
      setHasFileUploadError(false)
      setUploadPercentage(0)
    },
    onUpdateRequest: async () => {
      const endpoint = privacyCenterContent?.endpoints?.services?.updateServiceFiles

      await submitPreparedFiles({
        endpoint,
        flow: 'update_request_files',
        step: 'on_update_request',
        onSubmit: (updateServiceRequestEndpoint, ticketFiles) =>
          updateMutation.mutateAsync({
            protocol,
            ticketFiles,
            endpoint: updateServiceRequestEndpoint,
          }),
      })
    },
    onSendRequest: async () => {
      const endpoint = privacyCenterContent?.endpoints?.services?.sendServiceRequest

      await submitPreparedFiles({
        endpoint,
        flow: 'send_request_files',
        step: 'on_send_request',
        onSubmit: (sendServiceRequestEndpoint, ticketFiles) =>
          newRequestMutation.mutateAsync({
            newResquestData: {
              subject: selectedValue.value.toString(),
              details: textValue,
              ticketFiles,
            },
            endpoint: sendServiceRequestEndpoint,
          }),
      })
    },
  }
}
